import { TagType } from '@shared/components/generic-tag/generic-tag.component';

export interface ColumnConfig {
    field: string;
    header: string;
    width?: string;
    sortable?: boolean;
    type?: 'text' | 'date' | 'tag' | 'custom' | 'link' | 'boolean' | 'color';
    tagType?: TagType; // for generic-p-tag with enum
    tagColor?: string; // field path for custom tag background color
    tagTextColor?: string; // field path for custom tag text color
    dateFormat?: string;
    linkText?: string; // Optional text to display for link type (defaults to field value)
    linkTarget?: '_blank' | '_self' | '_parent' | '_top'; // Link target (defaults to '_blank')
    styleClass?: string; // CSS class(es) to apply to the cell content wrapper
}

// New action interface similar to edit-create-dialog
export interface TableAction<T = any> {
    label?: string;
    icon?: string;
    tooltip?: string;
    severity?: 'primary' | 'secondary' | 'success' | 'info' | 'warn' | 'danger';
    styleClass?: string;
    disabled?: (item: T) => boolean;
    onClick: (item: T, event?: Event) => void | Promise<void>;
    useDefaultStyle?: 'add' | 'edit' | 'delete';
    confirmMessage?: string; // For delete confirmation
    location: 'row' | 'toolbar'; // Extensible location system
    permission?: string; // Permission code required to show this action
}

export interface SelectionConfig<T = any> {
    mode: 'single' | 'multiple' | 'none';
    metaKeySelection?: boolean;
    dataKey?: string;
    onSelect?: (item: T, originalEvent: Event) => void;
    onUnselect?: (item: T, originalEvent: Event) => void;
}

export interface TableFeatures {
    virtualScroll?: boolean;
    pagination?: boolean;
    toolbar?: boolean;
    selection?: SelectionConfig;
    loading?: boolean;
}

export interface TableConfig<T = any> {
    // Data source configuration
    useApiData?: boolean; // Flag to toggle between API loading (true/default) and variable data (false)
    service?: any; // Optional when useApiData is false
    method?: string; // Optional when useApiData is false

    // Table metadata
    title: string;
    entityName: string; // for empty message, pagination text

    // Column definitions
    columns: ColumnConfig[];

    // Features configuration
    features?: TableFeatures;

    // Action buttons configuration - embedded functions like edit-create-dialog
    actions?: TableAction<T>[];

    // Additional service parameters
    additionalParams?: any;

    // Custom query builder for complex filtering
    queryBuilder?: (params: any) => any;

    // Response transformer
    transformResponse?: (response: any) => any;

    // Virtual scroll configuration
    virtualScrollItemSize?: number;
    virtualScrollOptions?: any;

    // Pagination configuration
    itemsPerPage?: number;
    itemsPerPageOptions?: number[];

    // POST request support (similar to generic-select)
    usePostMethod?: boolean; // Whether to use POST method with body data instead of GET with query params
    bodyBuilder?: (params: TableLazyLoadParams, baseQuery?: any, externalFilter?: any, internalFilter?: any, searchTerm?: string) => any; // Enhanced body builder for POST requests
    baseQuery?: any; // Static query that's always applied (e.g., departmentId filter)

    // URL path parameter support (for APIs like POST /getByDepartment/[departmentId])
    usePathParameter?: boolean; // Whether to extract path parameter from dependency value
    pathParameterExtractor?: (dependencyValue: any) => string | null; // Extract path parameter from dependency value

    // External dependency tracking (similar to generic-select)
    onDependencyChange?: (dependencyValue: any) => any; // Callback to transform dependency value into filter object

    // Filter toolbar configuration
    filterConfig?: any; // FilterToolbarConfig from generic-filter-toolbar (using any to avoid circular dependency)
}

export interface TableDataLoadEvent {
    items: any[];
    totalRecords: number;
    loading: boolean;
}

export interface TableActionEvent<T = any> {
    action: 'add' | 'edit' | 'delete' | 'custom';
    item?: T;
    customAction?: string;
    event?: Event;
}

export interface TableLazyLoadParams {
    first: number;
    rows: number;
    sortField?: string | string[] | null | undefined;
    sortOrder?: number | undefined | null;
    filters?: any;
}

// Type for supported table entity types
export type TableEntityType = 'probation' | 'employeePosition' | 'employee' | 'center';

export type TableConfigRegistry = Record<TableEntityType, () => Promise<TableConfigFactory>>;

// Factory function type for form configs
export type TableConfigFactory = () => TableConfig;

// Function mapping interface
export interface TableFunctionMap {
    OPEN_NEW?: () => void;
    EDIT?: (item: any) => void;
    DELETE?: (item: any) => void;
    ON_SELECT?: (item: any, event: Event) => void;
    ON_UNSELECT?: (item: any, event: Event) => void;
    RETIRE?: (item: any) => void;
    UPDATE_STATUS?: (item: any, event?: Event) => void;
    ADD_LEVEL?: (item: any) => void;
    CHANGE_CLUSTER?: (item: any) => void;
    CHANGE_SHIFT?: (item: any) => void;
    CHANGE_MINMAX?: (item: any) => void;
    [key: string]: any; // Allow custom function mappings
}

// Placeholder constants for function mapping
export const PLACEHOLDER_FUNCTIONS = {
    OPEN_NEW: '__PLACEHOLDER_OPEN_NEW__' as any,
    EDIT: '__PLACEHOLDER_EDIT__' as any,
    DELETE: '__PLACEHOLDER_DELETE__' as any,
    ON_SELECT: '__PLACEHOLDER_ON_SELECT__' as any,
    ON_UNSELECT: '__PLACEHOLDER_ON_UNSELECT__' as any,
    RETIRE: '__PLACEHOLDER_RETIRE__' as any,
    UPDATE_STATUS: '__PLACEHOLDER_UPDATE_STATUS__' as any,
    ADD_LEVEL: '__PLACEHOLDER_ADD_LEVEL__' as any,
    CHANGE_CLUSTER: '__PLACEHOLDER_CHANGE_CLUSTER__' as any,
    CHANGE_SHIFT: '__PLACEHOLDER_CHANGE_SHIFT__' as any,
    CHANGE_MINMAX: '__PLACEHOLDER_CHANGE_MINMAX__' as any,
    CHANGE_TEAM: '__PLACEHOLDER_CHANGE_TEAM__' as any,
    TEAM_HISTORY: '__PLACEHOLDER_TEAM_HISTORY__' as any
};